/**
 * @fileoverview Entity Picker data source
 * backed by ajax
 */
// @ts-nocheck
goog.declareModuleId('yext.ui.components.entitypicker.datasource.ajaxsource');

import {DataModel} from '/ui/components/entitypicker/datasource/datamodel';
import {DataSource} from '/ui/components/entitypicker/datasource/datasource';
import {DataJson} from '/ui/components/entitypicker/datasource/jsontypes';
import {logError} from '/ui/lib/errors';

const MAX_QUERY_WITH_ID_SIZE = 200;

/**
 * @implements {DataSource}
 */
export class AjaxDataSource {
  constructor(
    url,
    fields,
    permissions,
    activationFilterType,
    features,
    sortOrderId,
    includeArchivedEntities,
    useAdvancedEntityGrouping,
  ) {
    this.url = url;
    this.fields = fields;
    this.permissions = permissions;
    this.activationFilterType = activationFilterType;
    this.features = features;
    this.sortOrderId = sortOrderId;
    this.currentSearchRequest = null;
    this.currentFetchRequests = [];
    this.includeArchivedEntities = includeArchivedEntities;
    this.useAdvancedEntityGrouping = useAdvancedEntityGrouping;
  }

  /**
   * @override
   */
  searchWithTerm(term, entityTypesFilter, entityIdsFilter, savedSearchFilter, compoundSearchFilter) {
    const query = this.buildSearchTermParams(term, entityTypesFilter, entityIdsFilter, savedSearchFilter, compoundSearchFilter);
    if (this.currentSearchRequest) {
      this.cancelCurrentSearchRequest();
    }
    return new Promise((resolve, reject) => {
      this.currentSearchRequest = this.xhrRequestHelper(query, resolve, reject);
    }).then(data => DataModel.fromJson(data));
  }

  buildSearchTermParams(term, entityTypesFilter, entityIdsFilter, savedSearchFilter, compoundSearchFilter) {
    let serializedCompoundSearchFilter = undefined;
    if (!!compoundSearchFilter) {
      if (!!compoundSearchFilter?.serializeBinary) {
        serializedCompoundSearchFilter = btoa(
          String.fromCharCode.apply(null, compoundSearchFilter.serializeBinary()));
      } else {
        console.error('Unrecognized compoundSearchFilter ' + compoundSearchFilter);
      }
    }
    return buildQueryDataFromMap({
      'term': term,
      'field': this.fields,
      'permission': this.permissions,
      'activationFilterType': this.activationFilterType,
      'feature': this.features,
      'typeFilter': entityTypesFilter,
      'sort': this.sortOrderId,
      'includeArchivedEntities': this.includeArchivedEntities,
      'useAdvancedEntityGrouping': this.useAdvancedEntityGrouping,
      'entityIdsFilter': entityIdsFilter,
      'savedSearchFilter': savedSearchFilter,
      'compoundSearchFilter': serializedCompoundSearchFilter,
    });
  }

  /**
   * @override
   */
  searchWithIds(entityTypes, entities, folders, labels) {
    const queryParams = getQueryParams(entityTypes, entities, folders, labels);
    const queries = [];
    for (let i = 0; i < queryParams.length; i += MAX_QUERY_WITH_ID_SIZE) {
      const queryParamArray = queryParams.slice(i, i + MAX_QUERY_WITH_ID_SIZE);
      queryParamArray.push(buildQueryArraySuffix('permission', this.permissions));
      queryParamArray.push(buildQueryArraySuffix('activationFilterType', this.activationFilterType));
      queryParamArray.push(buildQueryArraySuffix('includeArchivedEntities', this.includeArchivedEntities));
      queryParamArray.push(buildQueryArraySuffix('useAdvancedEntityGrouping', this.useAdvancedEntityGrouping));
      const query = queryParamArray.join('');
      queries.push(query.substring(1));
    }
    const promises = queries.map(query => new Promise((resolve, reject) => {
      this.addCurrentFetchRequest(this.xhrRequestHelper(query, resolve, reject));
    }));
    return Promise.all(promises).then(data => data.length > 1
      ? DataModel.fromJson(mergeFetchedData(data))
      : DataModel.fromJson(data[0]),
    );
  }

  /**
   * @override
   */
  searchWithQuery(query) {
    return new Promise((resolve, reject) => {
      this.addCurrentFetchRequest(this.xhrRequestHelper(query, resolve, reject));
    }).then(data => DataModel.fromJson(data));
  }

  xhrRequestHelper(query, resolve, reject) {
    const controller = new AbortController();
    const signal = controller.signal;

    fetch(`${this.url}?includeEmptyEntityTypes=true&${query}`, {
      method: 'GET',
      headers: {'X-Requested-With': 'XMLHttpRequest'},
      signal: signal,
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 403) {
            reject(false); // Ignore 403 errors (e.g., when the auth token expires)
          } else {
            logError(`EntityPicker: Bad Request with query: ${query}\n${response.statusText}`);
            reject(false);
          }
        }
      })
      .then(data => resolve(data))
      .catch(error => {
        if (error.name === 'AbortError') {
          reject(true); // Indicate the request was aborted
        } else {
          reject(false);
        }
      });

    // Return an object with the abort method, similar to what XhrIo provided
    return {
      abort() {
        controller.abort();
      },
    };
  }


  addCurrentFetchRequest(request) {
    this.currentFetchRequests.push(request);
  }

  cancelCurrentFetchRequests() {
    this.currentFetchRequests.forEach(request => request.abort());
    this.currentFetchRequests = [];
  }

  cancelCurrentSearchRequest() {
    this.currentSearchRequest.abort();
  }

  /**
   * @override
   */
  cancel() {
    if (this.currentFetchRequests.length) {
      this.cancelCurrentFetchRequests();
    }
    if (this.currentSearchRequest) {
      this.cancelCurrentSearchRequest();
    }
  }
}

/**
 * Takes in a param name and either an array of values
 * and creates a query suffix.
 * @param {string} paramName name of the query param
 * @param {Array<string|number>|undefined} array array of values to be associated with the param
 * @return {string} suffix required in string format for the query
 */
export function buildQueryArraySuffix(paramName, array) {
  return '&' + buildQueryDataFromMap({[paramName]: array});
}

/**
 * Takes in a param name and a single
 * value and creates a query suffix.
 * @param {string} paramName name of the query param
 * @param {string|number} queryValue value to be associated with the param
 * @return {string} suffix required in string format for the query
 */
export function buildQuerySuffix(paramName, queryValue) {
  return '&' + buildQueryDataFromMap({[paramName]: queryValue});
}

/**
 * takes in arrays of each item type and provides a mapping from
 * item id to the string type required for the paramName of the query
 * @param {!Array<number>|undefined} entityTypes array of entity type ids
 * @param {!Array<number>|undefined} entities array of entity ids
 * @param {!Array<number>|undefined} folders array of folder ids
 * @param {!Array<number>|undefined} labels array of label ids
 * @return {!Array<string>} an array containing the query param for each item type id
 */
function getQueryParams(entityTypes, entities, folders, labels) {
  const params = [];
  if (entityTypes && entityTypes.length) {
    entityTypes.forEach(id => params.push(buildQuerySuffix('entityType', id)));
  }
  if (entities && entities.length) {
    entities.forEach(id => params.push(buildQuerySuffix('entity', id)));
  }
  if (folders && folders.length) {
    folders.forEach(id => params.push(buildQuerySuffix('folder', id)));
  }
  if (labels && labels.length) {
    labels.forEach(id => params.push(buildQuerySuffix('label', id)));
  }
  return params;
}

/**
 * @param {!Array} dataModels data to combine, an array of DataModel objects
 * @return {!DataJson} a single DataModel JSON object combining all the data
 * from each fetched DataModel
 */
function mergeFetchedData(dataModels) {
  const emptyModel = {
    'entities': {
      'total': 0,
      'types': [],
    },
    'folders': {
      'total': 0,
      'items': [],
    },
    'labels': {
      'total': 0,
      'items': [],
    },
  };
  return dataModels.reduce(
    (combinedModel, currentModel) => combinedModel = mergeJsonData(combinedModel, currentModel),
    emptyModel,
  );
}

/**
 * merges the data of src into a clone of dest, adding the
 * types, entities, folders, and labels
 * to the dest if not already present
 * @param {!DataJson} dest JSON object to merge into
 * @param {!DataJson} src JSON object to merge from
 * @return {!DataJson} the merged JSON
 *
 * NOTE(amullings): This doesn't support merging folderEntities and
 * labelEntities, but this is fine for now since we're only using those fields
 * for single entity selectors. In the long run, it would make more sense to
 * make DataModel merging more performant and do the merging after parsing so
 * that we don't have to maintain both merge implementations.
 */
function mergeJsonData(dest, src) {
  const result = /** @type {!DataJson} */({...dest});
  const newTypes = [];
  for (const srcType of src['entities']['types']) {
    const destType = result['entities']['types'].find(t => t['id'] == srcType['id']);
    if (destType) {
      srcType['entities'].forEach(entity => {
        if (!destType['entities'].some(e => e['id'] == entity['id'])) {
          destType['entities'].push(entity);
        }
      });
      newTypes.push(destType);
    } else {
      newTypes.push(srcType);
    }
  }
  result['entities']['types'] = newTypes;

  for (const folder of src['folders']['items']) {
    if (!result['folders']['items'].some(f => f['id'] == folder['id'])) {
      result['folders']['items'].push(folder);
    }
  }

  for (const label of src['labels']['items']) {
    if (!result['labels']['items'].some(l => l['id'] == label['id'])) {
      result['labels']['items'].push(label);
    }
  }

  return result;
}

/**
 ____________________________________________________________________
 |                                                                  |
 | The following section of code is copied from Closure Library     |
 | and replaced goog:goog.uri.utils as the server side url          |
 | parsing code relies on its behavior. It is slightly refactored   |
 | to remove closure dependencies and update syntax to es6.         |
 |                                                                  |
 ____________________________________________________________________
 */


/**
 * Supported query parameter values by the parameter serializing utilities.
 *
 * If a value is null or undefined, the key-value pair is skipped, as an easy
 * way to omit parameters conditionally.  Non-array parameters are converted
 * to a string and URI encoded.  Array values are expanded into multiple
 * &key=value pairs, with each element stringized and URI-encoded.
 *
 * @typedef {*}
 */
let QueryValue;

/**
 * Copied from Closure Library
 * Builds a query data string from a map.
 * Currently, generates "&key&" for empty args.
 *
 * @param {!Object<string, QueryValue>} map An object where keys
 *     are URI-encoded parameter keys, and the values are arbitrary types
 *     or arrays. Keys with a null value are dropped.
 * @return {string} The encoded query string, in the form 'a=1&b=2'.
 */
const buildQueryDataFromMap = function(map) {
  const params = [];
  for (const key in map) {
    appendKeyValuePairs_(key, map[key], params);
  }
  return params.join('&');
};

/**
 * Copied from Closure Library and refactored to remove colsure dependencies
 *
 * Appends key=value pairs to an array, supporting multivalued objects.
 * @param {*} key The key prefix.
 * @param {QueryValue} value The value to serialize.
 * @param {!Array<string>} pairs The array to which the 'key=value' strings
 *     should be appended.
 * @private
 */
const appendKeyValuePairs_ = function(key, value, pairs) {
  if (Array.isArray(value)) {
    for (let j = 0; j < value.length; j++) {
      // Convert to string explicitly, to short circuit the null and array
      // logic in this function -- this ensures that null and undefined get
      // written as literal 'null' and 'undefined', and arrays don't get
      // expanded out but instead encoded in the default way.
      appendKeyValuePairs_(key, String(value[j]), pairs);
    }
  } else if (value != null) {
    // Skip a top-level null or undefined entirely.
    pairs.push(
      key +
      // Check for empty string. Zero gets encoded into the url as literal
      // strings.  For empty string, skip the equal sign, to be consistent
      // with UriBuilder.java.
      (value === '' ? '' : '=' + encodeURIComponent(String(value))));
  }
};
